import { useRef, useState, useLayoutEffect } from 'react'
import styled from '@emotion/styled'
import L1ErrorBlock from '../l1/L1ErrorBlock'
import L2ControlGridLayout from './L2ControlGridLayout'
import { φ } from '../../../lib/util'

const controlGridGap = (theme) => theme.spacing(1, 2.5)

const L2ControlGridFlexColumn = styled.div(({theme}) => ({
	display: 'flex',
	gap: controlGridGap(theme),
	flexDirection: 'column',
}))

const L2ControlGridFlexRow = styled.div(({theme}) => ({
	display: 'flex',
	gap: controlGridGap(theme),
	flexDirection: 'row',
	alignItems: 'center',
	flexWrap: 'wrap',
}))

const L2ControlGridFlowRow = styled.div(({theme, columnCount, maxColumnWidth}) => ({
	display: 'grid',
	gap: controlGridGap(theme),
	alignItems: 'center',
	gridTemplateColumns: `repeat(${columnCount}, minmax(10px, ${maxColumnWidth}))`,
}))

const L2ControlGridFlowColumn = styled.div(({theme, columnCount, maxColumnWidth, rowCount}) => ({
	display: 'grid',
	gap: controlGridGap(theme),
	alignItems: 'center',
	gridTemplateColumns: `repeat(${columnCount}, minmax(10px, ${maxColumnWidth}))`,
	gridAutoFlow: 'column',
	gridTemplateRows: `repeat(${rowCount}, auto)`,
}))

const L2ControlGridDecoratedRow = styled.div(({theme, columnCount}) => ({
	display: 'grid',
	gap: controlGridGap(theme),
	alignItems: 'center',
	gridTemplateColumns: `repeat(${parseInt(columnCount) - 1}, minmax(10px, 1fr)) min-content`,
	flex: 1,
}))

const L2ControlGridCustomRow = styled.div(({theme, gridTemplateColumns}) => ({
	display: 'grid',
	gap: controlGridGap(theme),
	alignItems: 'center',
	gridTemplateColumns,
}))

/**
 * 
 * @param {object} props
 * @param {string} [props.className]
 * @param {L2ControlGridLayout} props.layout
 * @param {number} [props.columnCount]
 * @param {import("react").ReactNode} props.children
 * @returns 
 */
const L2ControlGrid = ({className, layout, columnCount, children}) => {
	const ref = useRef(null)
	const [childCount, setChildCount] = useState(0)
	useLayoutEffect(() => {
		let refChildCount = 0
		if(ref.current) {
			refChildCount = ref.current.children.length 
		}
		if(childCount !== refChildCount) {
			setChildCount(refChildCount)
		}
	})
	switch(layout) {
		case L2ControlGridLayout.FlexColumn:
			return (
				<L2ControlGridFlexColumn
					ref={ref}
					className={className}
					children={children}
				/>
			)
		case L2ControlGridLayout.FlexRow:
			return (
				<L2ControlGridFlexRow
					ref={ref}
					className={className}
					children={children}
				/>
			)
		case L2ControlGridLayout.FlowRow:
			return (
				<L2ControlGridFlowRow
					ref={ref}
					className={className}
					columnCount={columnCount || childCount}
					maxColumnWidth="max-content"
					children={children}
				/>
			)
		case L2ControlGridLayout.FlowColumn:
			return (
				<L2ControlGridFlowColumn
					ref={ref}
					className={className}
					columnCount={columnCount || 1}
					maxColumnWidth="max-content"
					rowCount={columnCount ? Math.ceil(childCount / columnCount) : childCount}
					children={children}
				/>
			)
		case L2ControlGridLayout.EqualDividedRow:
			return (
				<L2ControlGridFlowRow
					ref={ref}
					className={className}
					columnCount={columnCount ? columnCount : childCount}
					maxColumnWidth="1fr"
					children={children}
				/>
			)
		case L2ControlGridLayout.EqualDividedColumn:
			return (
				<L2ControlGridFlowColumn
					ref={ref}
					className={className}
					columnCount={columnCount || 1}
					maxColumnWidth="1fr"
					rowCount={columnCount ? Math.ceil(childCount / columnCount) : childCount}
					children={children}
				/>
			)
		case L2ControlGridLayout.GoldenShortLongRow:
			return (
				<L2ControlGridCustomRow
					ref={ref}
					className={className}
					gridTemplateColumns={`${100-(100/φ)}fr ${100/φ}fr`}
					children={children}
				/>
			)
		case L2ControlGridLayout.DecoratedRow:
			return (
				<L2ControlGridDecoratedRow
					ref={ref}
					className={className}
					columnCount={columnCount || 2}
					children={children}
				/>
			)
		default:
			return <L1ErrorBlock title={`[L2ControlGrid] unknown layout '${layout}'`} />
	}
}

export default L2ControlGrid
